import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {DarkModeToggleComponent} from "../dark-mode-toggle/dark-mode-toggle.component";
import {LanguageHeaderComponent} from "../language-header/language-header.component";
import {DOCUMENT, NgIf} from "@angular/common";
import {Router, RouterLink} from "@angular/router";
import {SubscriptionHeaderComponent} from "../subscription-header/subscription-header.component";
import {BillingService} from "../../services/billing.service";
import {Store} from "@ngrx/store";
import {Subject, Subscription} from "rxjs";
import {AuthService} from "../../services/auth.service";
import {SubscriptionService} from "../../services/subscription.service";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {SessionService} from "../../services/session.service";
import {UiMigrationConfig, UiSwitchService} from "../../services/ui-switch.service";
import {PopupService} from "../../services/popup.service";

@Component({
  selector: 'app-header-right-side',
  standalone: true,
    imports: [
        DarkModeToggleComponent,
        LanguageHeaderComponent,
        NgIf,
        RouterLink,
        SubscriptionHeaderComponent
    ],
  templateUrl: './header-right-side.component.html',
  styleUrl: './header-right-side.component.scss',
  animations: [
    trigger('dropdownAnimation', [
      state('void', style({
        opacity: 0,
        transform: 'scale(0.95)'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'scale(1)'
      })),
      transition('void => visible', [
        animate('100ms ease-out')
      ]),
      transition('visible => void', [
        animate('75ms ease-in')
      ])
    ])
  ]
})
export class HeaderRightSideComponent implements OnDestroy {

  @Output() dataEmitter: EventEmitter<any> = new EventEmitter();

  subscription: any;
  user: any;
  isLoading: boolean = false;
  range: string = '10';
  linkedin = '';

  private logoutSubscription: Subscription = new Subscription();
  isLoggedIn: boolean = true;

  mediaQuery: MediaQueryList;
  isMenuOpen = false;
  isUserOpen = false;

  sidebarTabs = [
    { id: 1, name: 'Assistant', current: false },
    { id: 2, name: 'Edit', current: false },
    { id: 3, name: 'Notes', current: false },
    { id: 4, name: 'Emails', current: false },
    { id: 5, name: 'CRM', current: false },
  ]

  // Subject for unsubscribing from all observables
  private destroy$ = new Subject<void>();

  // Add a flag to prevent API calls during logout
  private isLoggingOut = false;

  // New UI migration (Phase 1)
  uiMigration: UiMigrationConfig | null = null;
  isSwitchingUi = false;

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT)
    private document: Document,
    private billingService: BillingService,
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    private store: Store,
    private router: Router,
    private elementRef: ElementRef,
    private sessionService: SessionService,
    private uiSwitchService: UiSwitchService,
    private popupService: PopupService
  ) {
    this.mediaQuery = window.matchMedia('(max-width: 768px)');

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    this.subscriptionService.subscriptionData().subscribe(subscription => {
      this.subscription = subscription;
    });

    this.uiSwitchService.config().subscribe(config => {
      this.uiMigration = config;
    });

  }

  changeSidebarTab(tabIndex: number) {
    this.sidebarTabs.forEach((tab) => {
      tab.current = false
    })

    this.sidebarTabs[tabIndex].current = true

    this.dataEmitter.emit(this.sidebarTabs);

    this.isMenuOpen = false
  }

  isMobile(): boolean {
    return this.mediaQuery.matches;
  }

  toggleUser() {
    this.isUserOpen = !this.isUserOpen;
  }

  async switchToNewUi() {
    if (this.isSwitchingUi) return;
    this.isSwitchingUi = true;
    try {
      await this.uiSwitchService.switchToNewUi();
    } catch (e) {
      console.error('[HeaderRightSide] Switching to the new UI failed', e);
      this.popupService.notify('Could not open the new UI. Please try again.', 'New UI', 'warning');
      this.isSwitchingUi = false;
    }
  }

  openBillingPortal() {
    this.billingService.openBillingPortal();
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  /* commented on 03.04.2025
  signOut(){
    console.log('[dashboard-header.component] logout')

    this.isLoggedIn = false;

    this.store.dispatch(logout());

    this.logoutSubscription.add(
      this.store.select(selectLogoutSuccess).subscribe(success => {
        if (success) {
          // console.log('Logout success');
          // handle logout success, e.g., navigate to login page

          localStorage.clear(); // Clear local storage
          this.router.navigate(['/login']); // Redirect to the login page

          this.popupService.notify('You have been successfully logout!', 'Logout successfully', 'success');

        }
      })
    );

    this.logoutSubscription.add(
      this.store.select(selectLogoutFailure).subscribe(error => {
        if (error) {
          // console.log('Logout failure', error);
          // handle logout failure, e.g., show error message
        }
      })
    );

  }
  */

  /**
   * Signs out the current user and resets application state
   */
  signOut(): void {
    this.isLoggingOut = true;
    this.sessionService.signOut();
  }

  // HostListener for closing the dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      // Close the dropdown if the click is outside the component
      this.isUserOpen = false;
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

}
